import { useEffect, useState } from "react";
import { motion, useReducedMotion } from "motion/react";
import { Mail, Lock, Eye, EyeOff, ArrowLeft, Sun, Moon, User, Check, AlertCircle, Quote, Tag, ArrowUpRight, Globe } from "lucide-react";
import { isSupabaseConfigured, supabase } from "../lib/supabaseClient";
import { Link } from "react-router";
import { useTheme } from "./ThemeProvider";
import { track } from "../lib/analytics";

// Mirrors the DB check constraint profiles_handle_format.
const HANDLE_REGEX = /^[a-z0-9][a-z0-9-]{0,29}$/;

function normalizeHandle(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 30);
}

type HandleStatus =
  | { kind: "idle" }
  | { kind: "checking" }
  | { kind: "invalid"; reason: string }
  | { kind: "available" }
  | { kind: "taken" };

type Mode = "signIn" | "signUp" | "reset" | "verifyCode" | "newPassword";

const SERIF = "'Fraunces', Georgia, serif";

const inputClass =
  "w-full rounded-xl border border-border/40 bg-background/70 text-foreground placeholder:text-muted-foreground/40 outline-none transition-all duration-200 focus:border-accent/60 focus:bg-background focus:shadow-[0_0_0_3px_color-mix(in_srgb,var(--accent)_14%,transparent)]";

const NOISE_URI =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='140' height='140'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='140' height='140' filter='url(%23n)'/></svg>\")";

/** Slow-drifting radial glow used to paint the ambient backdrop. */
function GlowBlob({
  color,
  className,
  drift,
  duration,
  still,
}: {
  color: string;
  className: string;
  drift: { x: number[]; y: number[] };
  duration: number;
  still: boolean;
}) {
  return (
    <motion.div
      aria-hidden
      className={`absolute rounded-full pointer-events-none ${className}`}
      style={{
        background: `radial-gradient(circle at center, color-mix(in srgb, ${color} 30%, transparent), transparent 70%)`,
        filter: "blur(60px)",
      }}
      animate={still ? undefined : { x: drift.x, y: drift.y, scale: [1, 1.12, 1] }}
      transition={{ duration, repeat: Infinity, repeatType: "mirror", ease: "easeInOut" }}
    />
  );
}

/**
 * A tiny sample thought card that bobs gently on the brand panel. Mirrors the
 * real LibraryCard's anatomy (serif title, body, tag pill, source line, and the
 * rich link-preview with a thumbnail) across a few variants so the sign-in
 * preview looks like the actual product. Kept to four cards, tucked into the
 * right/top/bottom margins so they never sit over the brand copy.
 */
type SampleCard = {
  variant: "titled" | "quote" | "link" | "richlink";
  title?: string;
  body: string;
  tag?: string;
  source?: string;
  /** richlink only: a CSS background used as the faux preview thumbnail. */
  image?: string;
};

function FloatingThought({
  card,
  className,
  rotate,
  delay,
  still,
  width = 232,
}: {
  card: SampleCard;
  className: string;
  rotate: number;
  delay: number;
  still: boolean;
  width?: number;
}) {
  const isRich = card.variant === "richlink";
  return (
    <motion.div
      aria-hidden
      className={`absolute pointer-events-none select-none rounded-2xl border border-border/50 bg-card/90 backdrop-blur-sm shadow-xl shadow-primary/10 overflow-hidden ${className}`}
      style={{ rotate: `${rotate}deg`, width }}
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: still ? 0 : [0, -9, 0] }}
      transition={{
        opacity: { duration: 0.9, delay },
        y: still
          ? { duration: 0.9, delay }
          : { duration: 6.5, delay, repeat: Infinity, repeatType: "mirror", ease: "easeInOut" },
      }}
    >
      {/* Rich link preview: a gradient "hero image" with the source overlaid,
          exactly like a real link card's thumbnail — no external asset needed. */}
      {isRich && (
        <div className="relative h-[98px] w-full" style={{ background: card.image }}>
          <div
            className="absolute inset-0"
            style={{ background: "radial-gradient(120% 90% at 18% 0%, rgba(255,255,255,0.30), transparent 55%)" }}
          />
          <div
            className="absolute inset-x-0 bottom-0 h-2/3"
            style={{ background: "linear-gradient(to top, rgba(0,0,0,0.28), transparent)" }}
          />
          {card.source && (
            <span
              className="absolute left-3 bottom-2.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/25 backdrop-blur-sm text-white/95"
              style={{ fontSize: "0.6rem", fontWeight: 500 }}
            >
              <Globe size={9} /> {card.source}
            </span>
          )}
        </div>
      )}

      <div className="p-4">
        {card.variant === "quote" && <Quote size={15} className="text-accent/40 mb-1.5" />}
        {card.title && (
          <h3 className="text-primary mb-1.5" style={{ fontFamily: SERIF, fontSize: "0.94rem", lineHeight: 1.3 }}>
            {card.title}
          </h3>
        )}
        <p
          className={isRich ? "text-foreground/70 line-clamp-2" : "text-foreground/75"}
          style={{
            fontFamily: SERIF,
            fontSize: card.variant === "quote" ? "0.9rem" : "0.84rem",
            lineHeight: 1.5,
            fontStyle: card.variant === "quote" ? "italic" : "normal",
          }}
        >
          {card.body}
        </p>
        {card.tag && (
          <div className="mt-2.5">
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-accent/8 text-accent/60"
              style={{ fontSize: "0.62rem" }}
            >
              <Tag size={9} /> {card.tag}
            </span>
          </div>
        )}
        {/* Plain-link variant keeps the inline source row; richlink shows it on
            the thumbnail instead. */}
        {card.source && !isRich && (
          <span
            className="inline-flex items-center gap-1 text-muted-foreground mt-2.5"
            style={{ fontSize: "0.68rem" }}
          >
            <ArrowUpRight size={10} /> {card.source}
          </span>
        )}
      </div>
    </motion.div>
  );
}

export function SignInPage() {
  const [mode, setMode] = useState<Mode>("signIn");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [username, setUsername] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [handleStatus, setHandleStatus] = useState<HandleStatus>({ kind: "idle" });
  const [otpCode, setOtpCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const { theme, toggleTheme } = useTheme();
  const reducedMotion = useReducedMotion() ?? false;

  const disabled = !isSupabaseConfigured || status === "loading";

  // Live availability check on the handle when in signUp mode.
  useEffect(() => {
    if (mode !== "signUp") return;
    const raw = username.trim();
    if (raw.length === 0) {
      setHandleStatus({ kind: "idle" });
      return;
    }
    const normalized = normalizeHandle(raw);
    if (!HANDLE_REGEX.test(normalized)) {
      setHandleStatus({
        kind: "invalid",
        reason: "Letters, numbers, and hyphens. Must start with a letter or number.",
      });
      return;
    }
    setHandleStatus({ kind: "checking" });
    let cancelled = false;
    const id = setTimeout(async () => {
      const { data, error } = await supabase.rpc("is_handle_available", { candidate: normalized });
      if (cancelled) return;
      if (error) {
        setHandleStatus({ kind: "idle" });
        return;
      }
      setHandleStatus(data ? { kind: "available" } : { kind: "taken" });
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, [username, mode]);

  function friendlyAuthError(error: { message: string; status?: number }): string {
    const msg = error.message.toLowerCase();

    // Password issues
    if (msg.includes("password") && (msg.includes("short") || msg.includes("least") || msg.includes("characters") || msg.includes("length"))) {
      return "Password must be at least 6 characters.";
    }
    if (msg.includes("weak password") || msg.includes("password is too")) {
      return "Please choose a stronger password.";
    }

    // Email issues
    if (msg.includes("already registered") || msg.includes("already been registered") || msg.includes("user already exists")) {
      return "An account with this email already exists. Try signing in instead.";
    }
    if (msg.includes("invalid email") || msg.includes("valid email")) {
      return "Please enter a valid email address.";
    }
    if (msg.includes("email not confirmed")) {
      return "Please check your email and confirm your account first.";
    }

    // Credentials
    if (msg.includes("invalid login") || msg.includes("invalid credentials") || msg.includes("wrong password")) {
      return "Incorrect email or password. Please try again.";
    }

    // Rate limiting
    if (msg.includes("rate") || msg.includes("too many") || msg.includes("exceeded") || error.status === 429) {
      return "Too many attempts. Please wait a moment and try again.";
    }

    // Email sending
    if (msg.includes("sending") || msg.includes("confirmation email")) {
      return "We couldn’t send the confirmation email. Please try again shortly.";
    }

    // Signup disabled
    if (msg.includes("signups not allowed") || msg.includes("signup is disabled")) {
      return "New signups are currently disabled. Please try again later.";
    }

    return error.message;
  }

  const handleSignIn = async () => {
    if (!email.trim() || !password) return;
    setStatus("loading");
    setError(null);
    setNotice(null);
    const { error } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });
    if (error) {
      setStatus("error");
      setError(friendlyAuthError(error));
      track("signin_failed", { reason: friendlyAuthError(error) });
    } else {
      setStatus("success");
      track("signin_succeeded");
    }
  };

  const handleSignUp = async () => {
    if (!email.trim() || !password || password !== confirmPassword) {
      setError("Passwords need to match.");
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    const normalizedHandle = normalizeHandle(username.trim());
    if (!username.trim()) {
      setError("Pick a username — it becomes your public @handle.");
      return;
    }
    if (!HANDLE_REGEX.test(normalizedHandle)) {
      setError("Username uses lowercase letters, numbers, and hyphens. Must start with a letter or number.");
      return;
    }
    if (handleStatus.kind === "taken") {
      setError("That username is already taken. Try another.");
      return;
    }
    setStatus("loading");
    setError(null);
    setNotice(null);
    track("signup_started", { has_first_name: !!firstName.trim(), has_last_name: !!lastName.trim() });
    const { data, error } = await supabase.auth.signUp({
      email: email.trim(),
      password,
      options: {
        data: {
          first_name: firstName.trim() || null,
          last_name: lastName.trim() || null,
          username: normalizedHandle,
        },
      },
    });
    if (error) {
      setStatus("error");
      setError(friendlyAuthError(error));
      track("signup_failed", { reason: friendlyAuthError(error) });
    } else {
      setStatus("success");
      track("signup_completed", { requires_email_confirmation: !data.session, handle: normalizedHandle });
      // If email confirmation is enabled, Supabase returns a null session.
      // Give a clear confirmation and bring them back to Sign In.
      if (!data.session) {
        setNotice("Account created. Check your email to confirm, then sign in.");
        setMode("signIn");
        setPassword("");
        setConfirmPassword("");
        setStatus("idle");
      } else {
        // Session exists → RootLayout/AuthProvider will transition into the app.
        setNotice("Account created. Redirecting…");
      }
    }
  };

  const handleReset = async () => {
    if (!email.trim()) return;
    setStatus("loading");
    setError(null);
    setNotice(null);
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim());
    if (error) {
      setStatus("error");
      setError(friendlyAuthError(error));
    } else {
      setStatus("idle");
      setMode("verifyCode");
      setNotice("We sent a 6-digit code to your email.");
      track("password_reset_requested");
    }
  };

  const handleVerifyCode = async () => {
    if (!otpCode.trim() || otpCode.trim().length < 6) {
      setError("Please enter the 6-digit code from your email.");
      return;
    }
    setStatus("loading");
    setError(null);
    const { error } = await supabase.auth.verifyOtp({
      email: email.trim(),
      token: otpCode.trim(),
      type: "recovery",
    });
    if (error) {
      setStatus("error");
      setError("Invalid or expired code. Please try again.");
    } else {
      setStatus("idle");
      setMode("newPassword");
      setNotice(null);
      track("password_reset_code_verified");
    }
  };

  const handleSetNewPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setError("Passwords don’t match.");
      return;
    }
    setStatus("loading");
    setError(null);
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) {
      setStatus("error");
      setError("Failed to update password. Please try again.");
    } else {
      // Sign out so user logs in fresh with new password
      track("password_updated");
      await supabase.auth.signOut();
      setStatus("idle");
      setMode("signIn");
      setNotice("Password updated. Sign in with your new password.");
      setNewPassword("");
      setConfirmNewPassword("");
      setOtpCode("");
    }
  };

  const primaryActionLabel =
    mode === "signIn" ? "Sign in"
    : mode === "signUp" ? "Start free trial"
    : mode === "verifyCode" ? "Verify code"
    : mode === "newPassword" ? "Set new password"
    : "Send reset code";

  const handlePrimary = () => {
    if (mode === "signIn") return handleSignIn();
    if (mode === "verifyCode") return handleVerifyCode();
    if (mode === "newPassword") return handleSetNewPassword();
    if (mode === "signUp") return handleSignUp();
    return handleReset();
  };

  const heading =
    mode === "signIn" ? "Welcome back"
    : mode === "signUp" ? "Start your free trial"
    : mode === "verifyCode" ? "Enter your code"
    : mode === "newPassword" ? "Set new password"
    : "Reset your password";

  const subheading =
    mode === "signIn" ? "Sign in to continue to Burrow"
    : mode === "signUp" ? "14 days free, then pick a plan"
    : mode === "verifyCode" ? `We sent a 6-digit code to ${email}`
    : mode === "newPassword" ? "Choose a new password for your account"
    : "We’ll send you a code to reset your password";

  const switchMode = (next: Mode) => {
    setMode(next);
    setStatus("idle");
    setError(null);
    setNotice(null);
  };

  return (
    <div className="relative min-h-dvh bg-background overflow-hidden">
      {/* ── Ambient backdrop ─────────────────────────────────────────── */}
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <GlowBlob
          color="var(--accent)"
          className="w-[34rem] h-[34rem] -top-32 -left-24"
          drift={{ x: [0, 60, 0], y: [0, 40, 0] }}
          duration={26}
          still={reducedMotion}
        />
        <GlowBlob
          color="#C9A96E"
          className="w-[30rem] h-[30rem] bottom-[-8rem] left-[30%]"
          drift={{ x: [0, -50, 0], y: [0, -30, 0] }}
          duration={32}
          still={reducedMotion}
        />
        <GlowBlob
          color="var(--primary)"
          className="w-[28rem] h-[28rem] -right-24 top-[20%]"
          drift={{ x: [0, -40, 0], y: [0, 50, 0] }}
          duration={29}
          still={reducedMotion}
        />
        {/* film grain */}
        <div className="absolute inset-0 opacity-[0.035] mix-blend-multiply dark:mix-blend-screen" style={{ backgroundImage: NOISE_URI }} />
      </div>

      {/* Theme toggle */}
      <button
        type="button"
        onClick={toggleTheme}
        className="fixed top-7 right-7 z-30 w-9 h-9 inline-flex items-center justify-center rounded-full border border-border/40 bg-card/60 backdrop-blur text-muted-foreground hover:text-foreground transition-colors"
        aria-label={theme === "light" ? "Switch to dark mode" : "Switch to light mode"}
      >
        {theme === "light" ? <Moon size={15} /> : <Sun size={15} />}
      </button>

      <div className="relative z-10 min-h-dvh mx-auto max-w-6xl grid lg:grid-cols-[1.05fr_1fr] items-center gap-10 px-4 lg:px-12">
        {/* ── Brand panel (desktop only) ─────────────────────────────── */}
        <div className="relative hidden lg:block self-stretch">
          <div className="sticky top-0 flex h-dvh flex-col justify-center pr-4">
            {/* Copy sits in its own layer above the floating cards. Without the
                explicit z-index the cards — which come later in the DOM —
                painted straight over the headline once the panel narrowed at
                the lg breakpoint. The glass panel behind it keeps every line
                readable no matter which card drifts past. */}
            <div className="relative z-10 max-w-md">
              <div
                aria-hidden
                className="pointer-events-none absolute -inset-x-7 -inset-y-7 rounded-[2rem] border border-border/25 bg-background/55 backdrop-blur-xl shadow-xl shadow-primary/5"
              />
            <motion.h1
              className="relative text-primary"
              style={{ fontFamily: SERIF, fontSize: "4.2rem", fontWeight: 400, lineHeight: 1.05, letterSpacing: "-0.01em" }}
              aria-label="Burrow"
              initial="hidden"
              animate="shown"
              transition={{ staggerChildren: 0.045 }}
            >
              {"Burrow".split("").map((ch, i) => (
                <motion.span
                  key={i}
                  aria-hidden
                  className="inline-block"
                  variants={{
                    hidden: { opacity: 0, y: reducedMotion ? 0 : 14 },
                    shown: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.2, 0.65, 0.3, 1] } },
                  }}
                >
                  {ch}
                </motion.span>
              ))}
            </motion.h1>

            <motion.p
              className="relative text-foreground/80 mt-4"
              style={{ fontFamily: SERIF, fontSize: "1.35rem", fontStyle: "italic", lineHeight: 1.5 }}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.35 }}
            >
              A space for thinking
            </motion.p>

            <motion.p
              className="relative text-muted-foreground mt-3 max-w-sm"
              style={{ fontSize: "0.95rem", lineHeight: 1.7 }}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              Capture what crosses your mind. Let it settle into collections, wander into
              trails, and grow into editions worth sharing.
            </motion.p>
            </div>

            {/* Floating sample thoughts — z-0 keeps them strictly behind the copy. */}
            <div className="absolute inset-0 z-0" aria-hidden>
              {/* Spread across the four corners of the brand panel so the
                  centered copy always sits in clear space between them. */}
              {/* Top-left — titled card. */}
              <FloatingThought
                card={{
                  variant: "titled",
                  title: "Mid-walk ideas",
                  body: "the best ideas arrive unannounced, past the third block",
                  tag: "field notes",
                }}
                className="left-[3%] top-[6%]"
                rotate={-3}
                delay={0.7}
                width={224}
                still={reducedMotion}
              />
              {/* Top-right — small quote, tucked into the top corner. */}
              <FloatingThought
                card={{
                  variant: "quote",
                  body: "a trail is just thoughts holding hands",
                }}
                className="right-0 top-[9%]"
                rotate={3}
                delay={0.95}
                width={196}
                still={reducedMotion}
              />
              {/* Bottom-left — the rich link preview hero. */}
              <FloatingThought
                card={{
                  variant: "richlink",
                  title: "How to do Creative Visual Research",
                  body: "A framework for approaching visual creative research in 2026.",
                  source: "artdirection.substack.com",
                  image:
                    "linear-gradient(135deg, var(--accent), color-mix(in srgb, var(--primary) 65%, var(--accent)) 60%, #C9A96E)",
                }}
                className="left-[6%] bottom-[7%]"
                rotate={-2}
                delay={1.15}
                width={244}
                still={reducedMotion}
              />
              {/* Bottom-right — plain link card with inline source. */}
              <FloatingThought
                card={{
                  variant: "link",
                  title: "On keeping the mess",
                  body: "publish the edition, keep the mess",
                  source: "burrow.world",
                }}
                className="right-2 bottom-[24%]"
                rotate={2}
                delay={1.35}
                width={206}
                still={reducedMotion}
              />
            </div>
          </div>
        </div>

        {/* ── Auth column ────────────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.2, 0.65, 0.3, 1] }}
          className="w-full max-w-[460px] mx-auto py-10"
        >
          {/* Mobile brand header */}
          <div className="text-center mb-8 lg:hidden">
            <h1 className="text-primary" style={{ fontFamily: SERIF, fontSize: "2.4rem", fontWeight: 400 }}>
              Burrow
            </h1>
            <p className="text-muted-foreground mt-1" style={{ fontFamily: SERIF, fontStyle: "italic", fontSize: "0.95rem" }}>
              A space for thinking
            </p>
          </div>

          {/* Card */}
          <div className="relative rounded-3xl border border-border/50 bg-card/85 backdrop-blur-xl px-6 py-6 md:px-8 md:py-8 shadow-2xl shadow-primary/10">
            {/* hairline highlight along the top edge */}
            <div
              aria-hidden
              className="absolute inset-x-8 top-0 h-px"
              style={{ background: "linear-gradient(90deg, transparent, color-mix(in srgb, var(--accent) 55%, transparent), transparent)" }}
            />

            {/* Back link */}
            {mode !== "signIn" && (
              <button
                type="button"
                onClick={() => {
                  if (mode === "verifyCode") switchMode("reset");
                  else if (mode === "newPassword") switchMode("verifyCode");
                  else switchMode("signIn");
                }}
                className="mb-4 inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors"
                style={{ fontSize: "0.82rem", fontFamily: SERIF, fontStyle: "italic" }}
              >
                <ArrowLeft size={14} />
                {mode === "verifyCode" || mode === "newPassword" ? "back" : "back to sign in"}
              </button>
            )}

            <motion.div
              key={mode}
              initial={{ opacity: 0, y: reducedMotion ? 0 : 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.32, ease: "easeOut" }}
            >
              <h2 className="text-primary" style={{ fontFamily: SERIF, fontSize: "1.55rem", fontWeight: 400 }}>
                {heading}
              </h2>
              <p className="text-muted-foreground mt-1 mb-6" style={{ fontSize: "0.9rem" }}>
                {subheading}
              </p>

              {!isSupabaseConfigured && (
                <div className="mb-4 rounded-xl border border-destructive/20 bg-destructive/5 px-4 py-3">
                  <p className="text-destructive/80" style={{ fontSize: "0.85rem", lineHeight: "1.5" }}>
                    Supabase isn’t configured for this environment. Set{" "}
                    <code className="font-mono">VITE_SUPABASE_URL</code> and{" "}
                    <code className="font-mono">VITE_SUPABASE_ANON_KEY</code> to enable sign-in.
                  </p>
                </div>
              )}

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handlePrimary();
                }}
              >
                {/* Form fields */}
                {mode === "signUp" && (
                  <>
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div>
                        <label htmlFor="auth-first-name" className="block text-muted-foreground/70 mb-1" style={{ fontSize: "0.78rem" }}>
                          First name
                        </label>
                        <input
                          id="auth-first-name"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          className={`${inputClass} px-3 py-2.5`}
                          style={{ fontSize: "0.9rem" }}
                        />
                      </div>
                      <div>
                        <label htmlFor="auth-last-name" className="block text-muted-foreground/70 mb-1" style={{ fontSize: "0.78rem" }}>
                          Last name
                        </label>
                        <input
                          id="auth-last-name"
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          className={`${inputClass} px-3 py-2.5`}
                          style={{ fontSize: "0.9rem" }}
                        />
                      </div>
                    </div>

                    <div className="mb-3">
                      <label htmlFor="auth-username" className="block text-muted-foreground/70 mb-1" style={{ fontSize: "0.78rem" }}>
                        Username
                      </label>
                      <div className="relative">
                        <User size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/40" />
                        <input
                          id="auth-username"
                          value={username}
                          onChange={(e) => setUsername(e.target.value)}
                          onBlur={() => setUsername((v) => normalizeHandle(v.trim()))}
                          className={`${inputClass} pl-9 pr-10 py-2.75 ${
                            handleStatus.kind === "taken" || handleStatus.kind === "invalid"
                              ? "border-destructive/40 focus:border-destructive/60"
                              : handleStatus.kind === "available"
                              ? "border-accent/50 focus:border-accent/70"
                              : ""
                          }`}
                          placeholder="your-handle"
                          style={{ fontSize: "0.9rem" }}
                          autoCapitalize="none"
                          autoCorrect="off"
                          spellCheck={false}
                        />
                        <HandleStatusIndicator status={handleStatus} />
                      </div>
                      <HandleStatusHint status={handleStatus} normalized={normalizeHandle(username.trim())} username={username} />
                    </div>
                  </>
                )}

                {mode !== "verifyCode" && mode !== "newPassword" && (
                  <div className="mb-3">
                    <label htmlFor="auth-email" className="block text-muted-foreground/70 mb-1" style={{ fontSize: "0.78rem" }}>
                      Email address
                    </label>
                    <div className="relative">
                      <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/40" />
                      <input
                        id="auth-email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className={`${inputClass} pl-9 pr-3 py-2.75`}
                        placeholder="you@domain.com"
                        style={{ fontSize: "0.9rem" }}
                      />
                    </div>
                  </div>
                )}

                {(mode === "signIn" || mode === "signUp") && (
                  <div className="mb-3">
                    <label htmlFor="auth-password" className="block text-muted-foreground/70 mb-1" style={{ fontSize: "0.78rem" }}>
                      Password
                    </label>
                    <div className="relative">
                      <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/40" />
                      <input
                        id="auth-password"
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className={`${inputClass} pl-9 pr-9 py-2.75`}
                        placeholder="••••••••"
                        style={{ fontSize: "0.9rem" }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((s) => !s)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground/50 hover:text-foreground transition-colors"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                )}

                {mode === "signUp" && (
                  <div className="mb-3">
                    <label htmlFor="auth-confirm-password" className="block text-muted-foreground/70 mb-1" style={{ fontSize: "0.78rem" }}>
                      Confirm password
                    </label>
                    <input
                      id="auth-confirm-password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className={`${inputClass} px-3 py-2.75`}
                      placeholder="••••••••"
                      style={{ fontSize: "0.9rem" }}
                    />
                  </div>
                )}

                {/* OTP code entry */}
                {mode === "verifyCode" && (
                  <div className="mb-3">
                    <label htmlFor="auth-otp" className="block text-muted-foreground/70 mb-1" style={{ fontSize: "0.78rem" }}>
                      6-digit code
                    </label>
                    <input
                      id="auth-otp"
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      className={`${inputClass} px-3 py-2.75 text-center`}
                      placeholder="000000"
                      style={{ fontSize: "1.3rem", fontFamily: "monospace", letterSpacing: "0.5em" }}
                      autoFocus
                    />
                  </div>
                )}

                {/* New password fields */}
                {mode === "newPassword" && (
                  <>
                    <div className="mb-3">
                      <label htmlFor="auth-new-password" className="block text-muted-foreground/70 mb-1" style={{ fontSize: "0.78rem" }}>
                        New password
                      </label>
                      <div className="relative">
                        <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/40" />
                        <input
                          id="auth-new-password"
                          type={showPassword ? "text" : "password"}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className={`${inputClass} pl-9 pr-9 py-2.75`}
                          placeholder="••••••••"
                          style={{ fontSize: "0.9rem" }}
                          autoFocus
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((s) => !s)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground/50 hover:text-foreground transition-colors"
                          aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                          {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </div>
                    <div className="mb-3">
                      <label htmlFor="auth-confirm-new-password" className="block text-muted-foreground/70 mb-1" style={{ fontSize: "0.78rem" }}>
                        Confirm new password
                      </label>
                      <input
                        id="auth-confirm-new-password"
                        type="password"
                        value={confirmNewPassword}
                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                        className={`${inputClass} px-3 py-2.75`}
                        placeholder="••••••••"
                        style={{ fontSize: "0.9rem" }}
                      />
                    </div>
                  </>
                )}

                {mode === "signIn" && (
                  <div className="flex items-center justify-between mb-4">
                    <button
                      type="button"
                      onClick={() => switchMode("reset")}
                      className="ml-auto text-muted-foreground hover:text-accent text-xs transition-colors"
                    >
                      Forgot password?
                    </button>
                  </div>
                )}

                {error && (
                  <div className="mb-3 flex items-start gap-2 rounded-xl border border-destructive/20 bg-destructive/5 px-3.5 py-2.5">
                    <AlertCircle size={14} className="mt-0.5 shrink-0 text-destructive/80" />
                    <p className="text-destructive" style={{ fontSize: "0.82rem", lineHeight: 1.5 }}>
                      {error}
                    </p>
                  </div>
                )}
                {notice && (
                  <div className="mb-3 flex items-start gap-2 rounded-xl border border-accent/25 bg-accent/8 px-3.5 py-2.5">
                    <Check size={14} className="mt-0.5 shrink-0 text-accent" />
                    <p className="text-accent" style={{ fontSize: "0.82rem", lineHeight: 1.5 }}>
                      {notice}
                    </p>
                  </div>
                )}

                <motion.button
                  type="submit"
                  disabled={disabled}
                  whileHover={disabled ? undefined : { scale: 1.012, y: -1 }}
                  whileTap={disabled ? undefined : { scale: 0.985 }}
                  className="w-full mt-2 mb-4 py-3.5 rounded-2xl text-accent-foreground transition-shadow disabled:opacity-40 shadow-lg shadow-accent/25 hover:shadow-xl hover:shadow-accent/30"
                  style={{
                    fontSize: "0.94rem",
                    fontWeight: 500,
                    background: "linear-gradient(135deg, color-mix(in srgb, var(--accent) 88%, white), var(--accent) 45%, color-mix(in srgb, var(--accent) 72%, var(--primary)))",
                  }}
                >
                  {status === "loading" ? "Please wait…" : primaryActionLabel}
                </motion.button>
              </form>

              {/* Footer actions */}
              {mode === "signIn" && (
                <p className="text-center text-muted-foreground mt-2" style={{ fontSize: "0.82rem" }}>
                  Try Burrow free for 14 days.{" "}
                  <button
                    type="button"
                    onClick={() => switchMode("signUp")}
                    className="text-accent hover:underline"
                    style={{ fontSize: "0.82rem", fontWeight: 400 }}
                  >
                    Sign up
                  </button>
                </p>
              )}
              {mode === "signUp" && (
                <p className="text-center text-muted-foreground mt-2" style={{ fontSize: "0.82rem" }}>
                  Already have an account?{" "}
                  <button type="button" onClick={() => switchMode("signIn")} className="text-accent hover:underline">
                    Sign in
                  </button>
                </p>
              )}
            </motion.div>
          </div>

          {/* Legal footer */}
          <p className="mt-6 text-center text-muted-foreground/60" style={{ fontSize: "0.75rem" }}>
            By continuing, you agree to our{" "}
            <Link to="/terms" className="underline underline-offset-2 hover:text-foreground">
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link to="/privacy" className="underline underline-offset-2 hover:text-foreground">
              Privacy Policy
            </Link>
            .
          </p>
        </motion.div>
      </div>
    </div>
  );
}

function HandleStatusIndicator({ status }: { status: HandleStatus }) {
  if (status.kind === "available") {
    return <Check size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-accent" />;
  }
  if (status.kind === "taken" || status.kind === "invalid") {
    return <AlertCircle size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-destructive/80" />;
  }
  if (status.kind === "checking") {
    return (
      <div
        className="absolute right-3 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full border-2 border-muted-foreground/30 border-t-accent animate-spin"
        aria-hidden
      />
    );
  }
  return null;
}

function HandleStatusHint({
  status,
  normalized,
  username,
}: {
  status: HandleStatus;
  normalized: string;
  username: string;
}) {
  if (status.kind === "taken") {
    return (
      <p className="text-destructive/80 mt-1" style={{ fontSize: "0.74rem" }}>
        Taken. Try another.
      </p>
    );
  }
  if (status.kind === "invalid") {
    return (
      <p className="text-destructive/80 mt-1" style={{ fontSize: "0.74rem" }}>
        {status.reason}
      </p>
    );
  }
  if (status.kind === "available") {
    return (
      <p className="text-accent mt-1" style={{ fontSize: "0.74rem" }}>
        Available — your profile will be at <span className="font-medium">@{normalized}</span>.
      </p>
    );
  }
  if (username.trim().length === 0) {
    return (
      <p className="text-muted-foreground/60 mt-1" style={{ fontSize: "0.74rem" }}>
        Letters, numbers, hyphens. Becomes your public @handle.
      </p>
    );
  }
  return null;
}
